import {
	CasterLowTonnageReason,
	CasterTundish,
	CasterTundishShroud,
	Grade,
	Ladle,
} from "@/pages/caster/models/Catalogs";
import { ProcessData } from "@/pages/caster/models/ProcessData";
import { getMomentTz } from "@dexteel/mesf-core";
import { Pause, PlayArrow } from "@mui/icons-material";
import LinkIcon from "@mui/icons-material/Link";
import UnLinkIcon from "@mui/icons-material/LinkOff";
import ReceiptIcon from "@mui/icons-material/Receipt";
import RestoreIcon from "@mui/icons-material/Restore";
import {
	Box,
	Button,
	ButtonGroup,
	Grid2 as Grid,
	Paper,
	useTheme,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonWithLoading } from "../../../../../controls/buttons/ButtonWithLoading";
import { useCastersContext } from "../../CastersContext";
import { useSaveProcess } from "../../hooks/useSaveProcess";
import { OpenAndCloseButtons } from "../buttons/OpenAndCloseButtons";
import { HeatStatusControl } from "../controls/HeatStatusControl";
import { LinkUnlinkButton } from "../controls/LinkUnlinkButton";
import { LadleLifeTrimTempCheckbox } from "../form-fields/LadleLifeCheckbox";
import { TextFieldWithCheckbox } from "../form-fields/common/CheckboxFieldDE";
import { DateTimePickerDE } from "../form-fields/common/DateTimePickerDE";
import { DurationFieldDE } from "../form-fields/common/DurationFieldDE";
import { NumberTextFieldDE } from "../form-fields/common/NumberTextFieldDE";
import { NumberWithCheckboxFieldDE } from "../form-fields/common/NumberWithCheckboxFieldDE";
import { SelectorFieldDE } from "../form-fields/common/SelectorFieldDE";
import { TextFieldDE } from "../form-fields/common/TextFieldDE";
import { useCasterDENavigation } from "../hooks/useCasterDENavigation";
import { useFormHandlers } from "../hooks/useFormHandlers";
import { RequireGradeModal } from "../modals/require-grade/RequireGradeModal";
import { useStyles } from "../styles";
import { ProcessNavigatorWithSearch } from "../ui/ProcessNavigatorWithSearch";

const moment = getMomentTz();

type HeaderComponentProps = {
	process: ProcessData;
	ladles: Ladle[];
	grades: Grade[];
	tundishes: CasterTundish[];
	tundishShrouds: CasterTundishShroud[];
	tonnageReasons: CasterLowTonnageReason[];
	reloadProcess: Function;
	calculateProcess: Function;
	refreshData: Function;
	onProcessClicked: () => void;
	setDeliveryTicketModalOpen: (open: boolean) => void;
};

export const HeaderComponent = ({
	process,
	ladles,
	grades,
	tundishes,
	tundishShrouds,
	reloadProcess,
	calculateProcess,
	refreshData,
	onProcessClicked,
	setDeliveryTicketModalOpen,
}: HeaderComponentProps) => {
	const classes = useStyles();
	const theme = useTheme();
	const navigate = useNavigate();
	const { state, actions } = useCastersContext();

	const {
		handleOnNextProcess,
		handleOnPreviousProcess,
		handleOnLastProcess,
		handleGoToProcessesByShiftId,
		handleGoToProcess,
		handleGoToHeatsPage,
	} = useCasterDENavigation({ reloadProcess });

	const {
		handleBooleanValueChanged,
		handleOptionValueChanged,
		handleLadleValueChanged,
		handleGradeValueChanged,
		handleNumericValueChanged,
		handleOnBlurTextValue,
		handleOnBlurNumericValueForProcess,
		handleOnBlurNumericValueForHeat,
		handleBooleanCountersChanged,
		handleProcessUpdate,
		handleOnFocus,
		handleValueChanged,
	} = useFormHandlers({
		reloadProcess,
		refreshData,
	});

	const { saveGradeHeat, saveProcess } = useSaveProcess();

	const [showRequireGradeModal, setShowRequireGradeModal] = useState(false);
	const [isGradeSaving, setIsGradeSaving] = useState(false);
	const [pendingLadleClose, setPendingLadleClose] = useState<Date | null>(null);

	// maxDateTime updates with refreshStatus (every second) to block future times at minute precision
	const maxDateTime = useMemo(() => new Date(), [state.refreshStatus]);

	const handleRequireGradeModalClose = async (
		shouldUpdate: boolean,
		gradeId?: number,
	) => {
		if (shouldUpdate && gradeId) {
			setIsGradeSaving(true);
			await saveGradeHeat("GradeId", gradeId);

			if (pendingLadleClose && process.ProcessId) {
				const ladleOpen = moment.utc(process.LadleOpen);
				let ladleClose = moment.utc(pendingLadleClose);

				ladleClose.set("year", ladleOpen.get("year"));
				ladleClose.set("month", ladleOpen.get("month"));
				ladleClose.set("date", ladleOpen.get("date"));
				if (ladleClose < ladleOpen) {
					ladleClose.add(1, "days");
				}

				await saveProcess(
					process.ProcessId,
					ladleOpen.toDate(),
					ladleClose.toDate(),
				);
			}

			await refreshData();
			setIsGradeSaving(false);
		} else {
			await refreshData();
		}

		setShowRequireGradeModal(false);
		setPendingLadleClose(null);
	};

	const getShiftFormat = (
		shift: string,
		productionDate: Date,
		crew: string,
	) => {
		if (productionDate === null) return `${shift} ${crew}`;
		return `${moment(productionDate).format("MM/DD/yyyy")} ${shift} ${crew}`;
	};

	return (
		<Paper elevation={0}>
			<Box padding={2}>
				<Grid container size={{ xs: 12 }} spacing={2}>
					{/* Column 1: Process Navigator and Open/Close Buttons */}
					<Grid
						size={{ xs: 12, sm: 6, md: 2 }}
						container
						spacing={1}
						alignContent="flex-start"
					>
						<Grid size={{ xs: 12, md: 12 }}>
							<ProcessNavigatorWithSearch
								isProcessLoading={
									state.isLoadingPage || state.isLoadingBackground
								}
								value={process}
								secondValue={process.ProductionYear?.toString()}
								iconButton={
									process.HeatId ? (
										<UnLinkIcon style={{ cursor: "pointer" }} />
									) : (
										<LinkIcon style={{ cursor: "pointer" }} color="primary" />
									)
								}
								showNext={process.NextProcessId !== null}
								showPrevious={process.PrevProcessId !== null}
								onChange={(newProcessId) => handleGoToProcess(newProcessId)}
								onNext={handleOnNextProcess}
								onPrevious={handleOnPreviousProcess}
								onLast={handleOnLastProcess}
								onFocus={() => {
									actions.setRefreshInPause(true);
								}}
								onBlur={() => {
									actions.clearFocusField();
									actions.setRefreshInPause(false);
								}}
								onButtonClick={onProcessClicked} // Passed via props
							/>
						</Grid>
						<Grid size={{ xs: 12 }} container spacing={1} alignItems="center">
							<Grid size={{ xs: 8 }}>
								<HeatStatusControl heatStatus={process.HeatStatusCode} />
							</Grid>
							<Grid size={{ xs: 4 }}>
								<LinkUnlinkButton
									process={process}
									onButtonClick={onProcessClicked}
									loading={state.isLoadingBackground}
								/>
							</Grid>
						</Grid>

						<Grid size={{ xs: 12, md: 12 }}>
							<OpenAndCloseButtons
								refreshData={refreshData}
								process={process}
								grades={grades}
							/>
						</Grid>
					</Grid>

					{/* Column 2: Shift and Date Fields */}
					<Grid
						key={`first-${process?.ProcessId}`}
						size={{ xs: 12, sm: 6, md: 2 }}
						container
						spacing={1}
					>
						<Grid size={{ xs: 12 }} container direction="row" spacing={1}>
							{/* Shift */}
							<Grid size={{ xs: 12 }}>
								<TextFieldDE
									label="Shift"
									value={getShiftFormat(
										process.Shift,
										process.ProductionDate,
										process.Crew || "",
									)}
									disabled={true}
									onChange={(value) => {}}
									onBlur={(value) => {}}
									pause={() => actions.setRefreshInPause(true)}
									resume={() => actions.setRefreshInPause(false)}
								/>
							</Grid>

							{/* Ladle Open */}
							<Grid size={{ xs: 12 }}>
								<DateTimePickerDE
									label="Ladle Open"
									value={process.LadleOpen}
									format="HH:mm - DD/MM/YYYY"
									disabled={false}
									maxDateTime={maxDateTime}
									onChange={(date) => {
										handleValueChanged("LadleOpen", date);
									}}
									onBlur={(date) => {
										actions.setRefreshInPause(false);
										handleProcessUpdate();
									}}
									pause={() => actions.setRefreshInPause(true)}
									resume={() => actions.setRefreshInPause(false)}
								/>
							</Grid>

							<Grid size={{ xs: 12 }}>
								<DateTimePickerDE
									label="Ladle Close"
									value={process.LadleClose}
									format="HH:mm - DD/MM/YYYY"
									disabled={false}
									minDateTime={process.LadleOpen}
									maxDateTime={maxDateTime}
									onChange={(date) => {
										handleValueChanged("LadleClose", date);
									}}
									onBlur={(date) => {
										actions.setRefreshInPause(false);
										if (
											process.HeatId &&
											!process.GradeId &&
											process.LadleClose
										) {
											setPendingLadleClose(process.LadleClose);
											setShowRequireGradeModal(true);
											return;
										}
										handleProcessUpdate();
									}}
									pause={() => actions.setRefreshInPause(true)}
									resume={() => actions.setRefreshInPause(false)}
								/>
							</Grid>

							{/* Duration */}
							<Grid size={{ xs: 12 }}>
								<DurationFieldDE
									label="Duration [hh:mm:ss]"
									unit="seconds"
									value={process.Duration ? process.Duration : null}
									disabled={true}
									onChange={() => {}}
								/>
							</Grid>
						</Grid>
					</Grid>

					{/* Column 3: Process Data */}
					<Grid size={{ xs: 12, sm: 6, md: 2 }} container spacing={1}>
						<Grid size={{ xs: 12 }} container direction="row" spacing={1}>
							{/* Tap Time */}
							<Grid size={{ xs: 12 }}>
								<DateTimePickerDE
									label="Tap Time"
									value={process.TapTime}
									format="HH:mm - DD/MM/YYYY"
									disabled={true}
									disableOpenPicker
									onChange={(date) => {}}
									onBlur={(date) => {}}
									pause={() => actions.setRefreshInPause(true)}
									resume={() => actions.setRefreshInPause(false)}
								/>
							</Grid>

							{/* Steel Weight*/}
							<Grid size={{ xs: 12 }}>
								<NumberTextFieldDE
									label="Steel Weight [tons]"
									value={
										process.SteelWeight
											? parseFloat(process.SteelWeight.toFixed(1))
											: null
									}
									disabled={process.HeatId ? false : true}
									onChange={(val) => {
										handleValueChanged("SteelWeight", val);
									}}
									onBlur={() => {
										handleOnBlurNumericValueForHeat(
											"SteelWeight",
											process.SteelWeight?.toString() || "",
										);
									}}
									pause={() => actions.setRefreshInPause(true)}
									resume={() => actions.setRefreshInPause(false)}
								/>
							</Grid>
							{/* Grade */}
							<Grid size={{ xs: 12 }}>
								<SelectorFieldDE
									label="Grade"
									value={process.GradeId || ""}
									options={grades.map(({ GradeId, GradeName }) => ({
										value: GradeId,
										label: GradeName,
									}))}
									onChange={(value) =>
										handleGradeValueChanged("GradeId", Number(value))
									}
									disabled={!process.HeatId}
									nullable={false}
								/>
							</Grid>
							{/* Foreman */}
							<Grid size={{ xs: 12 }}>
								<TextFieldDE
									label="Foreman"
									value={process.Foreman || ""}
									disabled={false}
									onChange={(value) => handleValueChanged("Foreman", value)}
									onBlur={(value) => handleOnBlurTextValue("Foreman", value)}
									pause={() => actions.setRefreshInPause(true)}
									resume={() => actions.setRefreshInPause(false)}
								/>
							</Grid>
						</Grid>
					</Grid>
					{/* Column 4: Heat Data */}
					<Grid size={{ xs: 12, sm: 6, md: 2 }} container spacing={1}>
						<Grid size={{ xs: 12 }} container direction="row" spacing={1}>
							<Grid size={{ xs: 12 }}>
								<SelectorFieldDE
									label="Ladle"
									value={process.LadleId || ""}
									options={ladles.map(({ LadleId, LadleName }) => ({
										value: LadleId,
										label: LadleName,
									}))}
									onChange={(value) =>
										handleLadleValueChanged("LadleId", Number(value))
									}
									disabled={!process.HeatId}
									nullable={false}
								/>
							</Grid>

							<Grid size={{ xs: 12 }}>
								<LadleLifeTrimTempCheckbox
									ladleLife={{
										HeatsOnLadle: process.HeatsOnLadle || null,
										ContactTimeOnLadle: process.ContactTimeOnLadle || null,
									}}
									isSubmitLoading={state.isLoadingBackground}
									disabled
								/>
							</Grid>
							{/* Did Ladle Open*/}
							<Grid size={{ xs: 12 }}>
								<TextFieldWithCheckbox
									label="Did Ladle Open"
									checkboxValue={!!process.IsFreeOpen}
									disabled={false}
									onCheckboxChange={(checked) =>
										handleBooleanValueChanged("IsFreeOpen", checked)
									}
									onFocus={() => handleOnFocus("IsFreeOpen")}
									onBlur={() =>
										handleOnBlurTextValue(
											"IsFreeOpen",
											process.IsFreeOpen ? "true" : "false",
										)
									}
								/>
							</Grid>
							{/* First Heat*/}
							<Grid size={{ xs: 12 }}>
								<TextFieldWithCheckbox
									label="First Heat"
									checkboxValue={!!process.IsFirstHeat}
									disabled={false}
									onCheckboxChange={(checked) =>
										handleBooleanValueChanged("IsFirstHeat", checked)
									}
									onFocus={() => handleOnFocus("IsFirstHeat")}
									onBlur={() =>
										handleOnBlurTextValue(
											"IsFirstHeat",
											process.IsFirstHeat ? "true" : "false",
										)
									}
								/>
							</Grid>
						</Grid>
					</Grid>
					{/* Column 5: Checkboxes*/}
					<Grid size={{ xs: 12, sm: 6, md: 2 }} container spacing={1}>
						<Grid size={{ xs: 12 }} container direction="row" spacing={1}>
							{/* Tundish Selector */}
							<Grid size={{ xs: 6 }}>
								<SelectorFieldDE
									label="Tundish"
									value={process.TundishId || ""}
									options={tundishes.map(({ TundishId, TundishName }) => ({
										value: TundishId,
										label: TundishName,
									}))}
									onChange={(value) =>
										handleOptionValueChanged("TundishId", Number(value))
									}
									disabled={false}
									nullable={false}
								/>
							</Grid>
							{/* Heats on Tundish */}
							<Grid size={{ xs: 6 }}>
								<NumberWithCheckboxFieldDE
									label="Heats on Tundish"
									textFieldValue={process.HeatsOnTundish ?? 0}
									checkboxValue={!!process.ForceHeatsOnTundish}
									textFieldDisabled={true}
									checkboxDisabled={false}
									onNumberFieldChange={(val) =>
										handleNumericValueChanged(
											"HeatsOnTundish",
											val !== null ? val.toString() : "0", // Added null check
										)
									}
									onCheckboxChange={(checked) =>
										handleBooleanCountersChanged("ForceHeatsOnTundish", checked)
									}
									onBlur={() =>
										handleOnBlurNumericValueForProcess(
											"HeatsOnTundish",
											process.HeatsOnTundish?.toString() || "0",
										)
									}
									pause={() => actions.setRefreshInPause(true)}
									resume={() => actions.setRefreshInPause(false)}
									isSubmitLoading={state.isLoadingBackground}
								/>
							</Grid>
							{/* Nozzle Field */}
							<Grid size={{ xs: 6 }}>
								<NumberTextFieldDE
									label="Nozzle"
									value={
										process.NozzleSize !== null &&
										process.NozzleSize !== undefined
											? Number(process.NozzleSize)
											: null
									}
									onChange={(value) => {
										handleNumericValueChanged(
											"NozzleSize",
											value !== null ? value.toString() : "0",
										);
									}}
									onBlur={() => {
										actions.setRefreshInPause(false);
										handleOnBlurNumericValueForProcess(
											"NozzleSize",
											process.NozzleSize?.toString() || "0",
										);
									}}
									disabled={false}
									pause={() => actions.setRefreshInPause(true)}
									resume={() => actions.setRefreshInPause(false)}
								/>
							</Grid>
							{/* Heats on Nozzle Field with Checkbox */}
							<Grid size={{ xs: 6 }}>
								<NumberWithCheckboxFieldDE
									label="Heats on Nozzle"
									textFieldValue={process.HeatsOnNozzle ?? 0}
									checkboxValue={!!process.ForceHeatsOnNozzle}
									textFieldDisabled={true}
									checkboxDisabled={false}
									onNumberFieldChange={(val) =>
										handleNumericValueChanged(
											"HeatsOnNozzle",
											val !== null ? val.toString() : "0",
										)
									}
									onCheckboxChange={(checked) =>
										handleBooleanCountersChanged("ForceHeatsOnNozzle", checked)
									}
									onBlur={() =>
										handleOnBlurNumericValueForProcess(
											"HeatsOnNozzle",
											process.HeatsOnNozzle?.toString() || "0",
										)
									}
									pause={() => actions.setRefreshInPause(true)}
									resume={() => actions.setRefreshInPause(false)}
									isSubmitLoading={state.isLoadingBackground}
								/>
							</Grid>
							{/* Tundish Shroud Selector */}
							<Grid size={{ xs: 12 }}>
								<SelectorFieldDE
									label="Tundish Shroud"
									value={process.TundishShroudId || ""}
									options={tundishShrouds.map(
										({ TundishShroudId, TundishShroudName }) => ({
											value: TundishShroudId,
											label: TundishShroudName,
										}),
									)}
									onChange={(value) =>
										handleOptionValueChanged("TundishShroudId", Number(value))
									}
									disabled={false}
									nullable={false}
								/>
							</Grid>
							{/* Burn */}
							<Grid size={{ xs: 12 }}>
								<TextFieldWithCheckbox
									label="Burn"
									checkboxValue={!!process.IsBurnOpen}
									disabled={false}
									onCheckboxChange={(checked) =>
										handleBooleanValueChanged("IsBurnOpen", checked)
									}
									onFocus={() => handleOnFocus("IsBurnOpen")}
									onBlur={() =>
										handleOnBlurTextValue(
											"IsBurnOpen",
											process.IsBurnOpen ? "true" : "false",
										)
									}
								/>
							</Grid>
						</Grid>
					</Grid>

					{/* Column 6: Actions */}
					<Grid size={{ xs: 12, sm: 6, md: 2 }} container spacing={1}>
						<Grid size={{ xs: 12 }} container direction="row" spacing={1}>
							<Grid size={{ xs: 12 }}>
								<Button
									style={{
										backgroundColor: theme.palette.info.main,
										color: theme.palette.info.contrastText,
									}}
									variant="contained"
									size="large"
									onClick={() => handleGoToProcessesByShiftId(process.ShiftId)}
									fullWidth
								>
									PROCESSES
								</Button>
							</Grid>
							<Grid size={{ xs: 12 }}>
								<ButtonGroup fullWidth variant="contained" size="large">
									<Button
										disabled={!process.HeatShiftId || !process.HeatId}
										style={{
											backgroundColor: theme.palette.info.main,
											color: theme.palette.info.contrastText,
										}}
										onClick={() => {
											if (process.HeatShiftId && process.HeatId) {
												handleGoToHeatsPage(
													process.HeatShiftId,
													process.HeatId,
												);
											}
										}}
									>
										HEATS PAGE
									</Button>
									<Button
										disabled={!process.HeatId}
										style={{
											backgroundColor: theme.palette.info.main,
											color: theme.palette.info.contrastText,
										}}
										onClick={() => {
											const heatId = process.HeatId;
											if (heatId) {
												navigate(`/heat-report/${heatId}/caster`);
											}
										}}
									>
										HEAT REPORT
									</Button>
								</ButtonGroup>
							</Grid>
							<Grid size={{ xs: 12 }}>
								<ButtonGroup fullWidth>
									<ButtonWithLoading
										variant="contained"
										color="primary"
										size="large"
										onClick={() => calculateProcess()}
										isLoading={state.isLoadingBackground}
										style={{
											textTransform: "none",
											minWidth: "220px",
											maxWidth: "220px",
										}}
									>
										{state.entityEditting !== null ? (
											<span>REFRESH</span>
										) : (
											<>
												<span>REFRESHING</span>
												<span style={{ margin: "0 8px" }}>
													{"- " + state.refreshStatus + "s "}
												</span>
											</>
										)}
										<span>
											<RestoreIcon fontSize="small" />
										</span>
									</ButtonWithLoading>
									<Button
										onClick={() =>
											actions.setRefreshInPause((prev: boolean) => !prev)
										}
										variant="outlined"
										size="large"
									>
										{state.refreshInPause ? <PlayArrow /> : <Pause />}
									</Button>
								</ButtonGroup>
							</Grid>
							<Grid size={{ xs: 12 }}>
								<Button
									variant="contained"
									size="large"
									style={{
										backgroundColor: theme.palette.success.main,
										color: "white",
									}}
									fullWidth
									onClick={() => {
										setDeliveryTicketModalOpen(true);
										actions.setRefreshInPause(true);
									}}
									startIcon={<ReceiptIcon />}
								>
									Delivery Ticket
								</Button>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Box>
			<RequireGradeModal
				show={showRequireGradeModal}
				onHide={handleRequireGradeModalClose}
				grades={grades}
				isLoading={isGradeSaving}
			/>
		</Paper>
	);
};
